import {createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState} from "react"
import LoanReducer from "./LoanReducer";
import LoanApiService from "../api/api-service";

/**
 *  @typedef {Object} Loan
 *  @property {string}          fname
 *  @property {string}          lname
 *  @property {string}          email
 *  @property {string}          mobile
 *  @property {number}          amount
 *  @property {number}          duration
 *  @property {(null | number)} loanType
 */

const initState = {
    loanProducts: [],
    loading: false,
    error: "",
    setLoanType: (loanType) => null,
    loanType: {
        id: null,
        name: "",
        type_name: "",
        description: "" | null,
        created_at: "",
        updated_at: "",
        icon: "" | null,
        loan_child_type: []
    },
    setLoanChildType: (loanChildType) => null,
    loanChildType: {
        id: null,
        name: "",
        type_name: "",
        description: "" | null,
        loan_type_id: null,
        created_at: "",
        updated_at: "",
        loan_products: []
    },
    setLoanProduct: (loanProduct) => {
    },
    loanProduct: {
        id: null,
        name: "",
        icon: null,
        description: "",
        release_date: "",
        auto_payment: null,
        loan_duration_period: "",
        loan_interest_period: "",
        min_loan_duration: 0,
        def_loan_duration: 0,
        max_loan_duration: 0,
        min_principal_amount: 0.00,
        def_principal_amount: 0.00,
        max_principal_amount: 0.00,
        min_loan_interest: 0,
        def_loan_interest: 0,
        max_loan_interest: "",
        min_num_of_repayments: "",
        def_num_of_repayments: "",
        max_num_of_repayments: "",
        tag: "" | null,
        status: null,
        created_at: "",
        updated_at: "",
        icon_alt: "" | null,
        wiz_steps: null,
        loan_type: "",
        loan_child_type_id: null,
        is_digitize: null
    },
    setLoanDetails: (loanDetails) => {
    },
    /**
     * Loan Details
     * @type {Loan}
     */
    loanDetails: {
        fname: "",
        lname: "",
        email: "",
        mobile: "",
        amount: 0.00,
        duration: 0,
        loanType: null
    },
    createLoan: () => {
    },
    loadingLoanCreate: false,
    loanCreate: null,
    clientDetails: {
        fname: "",
        lname: "",
        email: "",
        mobile: "",
        nrc: "",
        address: ""
    },
    setClientDetails: () => {
    }
}

export const LoanContext = createContext(initState);

const createInitialState = () => {
    return initState;
}
export const LoanProvider = ({children}) => {
    const [state, dispatch] = useReducer(LoanReducer, initState, createInitialState);

    /** Loan Product
     *  @typedef {Object} LoanProduct
     *  @property {number}          id                             * Identifier                   *
     *  @property {number}          name                           * Name                         *
     *  @property {(null | any)}    icon                           * Icon                         *
     *  @property {(null | string)} description                    * Description                  *
     *  @property {string}          release_date                   * Release Date                 *
     *  @property {number}          auto_payment                   * Auto Payment                 *
     *  @property {string}          loan_duration_period           * Loan Duration Period         *
     *  @property {string}          loan_interest_period           * Loan Interest Period         *
     *  @property {string}          min_loan_duration              * Minimum Loan Duration        *
     *  @property {string}          def_loan_duration              * Default Loan Duration        *
     *  @property {string}          max_loan_duration              * Maximum Loan Duration        *
     *  @property {string}          min_principal_amount           * Minimum Principal Amount     *
     *  @property {string}          def_principal_amount           * Default Principal Amount     *
     *  @property {string}          max_principal_amount           * Maximum Principal Amount     *
     *  @property {string}          min_loan_interest              * Minimum Loan Interest        *
     *  @property {string}          def_loan_interest              * Default Loan Interest        *
     *  @property {string}          max_loan_interest              * Maximum Loan Interest        *
     *  @property {string}          min_num_of_repayments          * Minimum Number of Repayments *
     *  @property {string}          def_num_of_repayments          * Default Number of Repayments *
     *  @property {string}          max_num_of_repayments          * Maximum Number of Repayments *
     *  @property {(null | any)}    tag                            * Tag                          *
     *  @property {number}          status                         * Status                       *
     *  @property {string}          created_at                     * Date of Creation             *
     *  @property {string}          updated_at                     * Date of Update               *
     *  @property {(null | string)} icon_alt                       * Alternate Icon               *
     *  @property {number}          wiz_steps                      * Wiz Steps                    *
     *  @property {string}          loan_type                      * Loan Type                    *
     *  @property {number}          loan_child_type_id             * Loan Child Type ID           *
     *  @property {number}          is_digitize                    * Digitized?                   *
     */
    /** Loan Child Type
     *  @typedef {Object} LoanChildType
     *  @property {number}             id                         * Identifier               *
     *  @property {string}             name                       * Name                     *
     *  @property {string}             type_name                  * Name of Type             *
     *  @property {(null | string)}    description                * Description              *
     *  @property {string}             loan_type_id               * Loan Type ID             *
     *  @property {string}             created_at                 * Date of Creation         *
     *  @property {number}             updated_at                 * Date of Update           *
     *  @property {Array<LoanProduct>} [loan_products=[]]         * Loan Products (Optional) *
     */
    /** Loan Product Type
     *  @typedef {Object} LoanProductType
     *  @property {string}                name                     * Name                     *
     *  @property {(null | string)}      type_name                * Type Name                *
     *  @property {(null | string)}      description              * Description              *
     *  @property {string}               created_at               * Date of Creation         *
     *  @property {number}               updated_at               * Date of Update           *
     *  @property {(null | any)}         icon                     * Icon                     *
     *  @property {Array<LoanChildType>} loan_child_type          * Type of Child Loan       *
     */
    const [loanProducts, setLoanProducts] = useState(
        /**
         * Array of Loan Products
         * @type {(Array<LoanProductType> | null)}
         */
        []
    );

    const [error, setError] = useState(
        /** Loan Products provider operation errors
         *  @type {string}
         */
        ""
    );

    /** Loan Products fetch-activity-watcher
     *  @type {boolean}
     */
    const [loading, setLoading] = useState(false);

    const [loadingLoanCreate, setLoadingLoanCreate] = useState(false);

    const [loanCreate, setLoanCreate] = useState(
        /** Loan Products save-activity-watcher
         *  @type {(boolean | null)}
         */
        null
    );

    useEffect(() => {
        getLoanProductTypes();
    }, []);

    /**
     *  Get Loan Products Async Endpoint-Consumer-Caller
     *
     *  @description               - Get all loan products from the LMS platform.
     *  @returns {void}
     */
    const getLoanProductTypes = useCallback(() => {
        setLoading(true);
        LoanApiService.getLoans().then((loansPayload) => {
            if (Array.isArray(loansPayload)) { //success
                setLoanProducts(loansPayload);
            } else {
                const failedResponse = `${loansPayload}. Unable to fetch loan products from the LMS server!`;
                setError(failedResponse);
            }
        }).catch((error) => {
            console.log(error);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    /**
     *  Create Loan Products Async Endpoint-Consumer-Caller
     *
     *  @description          - Get all loan products from the LMS platform.
     *  @callback createLoan
     *  @returns {void}
     */
    const createLoan = useCallback(() => {
        setLoadingLoanCreate(true);
        const loan = state.loanDetails[0];
        LoanApiService.createLoan(loan).then((result) => {
            console.log("Res0: ", result);
            if (result === true) {
                console.log("Res1: ", result);
                setLoanCreate(result);
            } else {
                console.log("Res2: ", result);
                const failedResponse = `${result}. Unable to save loan application from the LMS server`;
                setError(failedResponse);
            }
        }).catch((error) => {
            console.log(error);
        }).finally(() => {
            // reset all
            setLoadingLoanCreate(false);
            if (loanCreate) {
                setLoanProduct(initState.loanProduct);
                setLoanChildType(initState.loanChildType);
                setLoanType(initState.loanType);
                setLoanDetails(initState.loanDetails);
            }
        });
    }, [])

    // Actions
    const setLoanType = useCallback((loanType) => {
        dispatch({
            type: 'SET_LOAN_TYPE',
            payload: loanType
        });
    }, []);

    const setLoanProduct = useCallback((loanProduct) => {
        dispatch({
            type: 'SET_LOAN_PRODUCT',
            payload: loanProduct
        });
    }, []);

    const setLoanChildType = useCallback((loanChildType) => {
        dispatch({
            type: 'SET_LOAN_CHILD_TYPE',
            payload: loanChildType
        });
    }, []);

    const setLoanDetails = useCallback((loanDetails) => {
        dispatch({
            type: 'SET_LOAN_DETAILS',
            payload: loanDetails
        });
    }, []);

    const setClientDetails = useCallback((clientDetails) => {
        dispatch({
            type: 'SET_CLIENT_DETAILS',
            payload: clientDetails
        });
    }, []);


    const loanCtxValues = useMemo(() => ({
        loanProducts,
        loading,
        error,
        setLoanType,
        loanType: state.loanType,
        setLoanChildType,
        loanChildType: state.loanChildType,
        setLoanProduct,
        loanProduct: state.loanProduct,
        setLoanDetails,
        loanDetails: state.loanDetails,
        createLoan,
        loadingLoanCreate,
        loanCreate,
        clientDetails: state.clientDetails,
        setClientDetails
    }), [
        loanProducts,
        loading,
        error,
        setLoanType,
        state.loanType,
        setLoanChildType,
        state.loanChildType,
        setLoanProduct,
        state.loanProduct,
        setLoanDetails,
        state.loanDetails,
        createLoan,
        loadingLoanCreate,
        loanCreate,
        state.clientDetails,
        setClientDetails
    ]);

    return (
        <LoanContext.Provider value={loanCtxValues}>
            {children}
        </LoanContext.Provider>
    )
}

export const useLoanProvider = () => {
    const loanContext = useContext(LoanContext);
    if (!loanContext) {
        const e = new Error('[useLoanProvider] must be used within the LoanContext.Provider');
        console.error(e);
    }
    return loanContext;
}
